"use strict";
/**
 * Page Navigation UI
 * Handles showing/hiding pages
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.showPage = showPage;
exports.syncLocation = syncLocation;
exports.initAppRouting = initAppRouting;
exports.__resetRoutingForTests = __resetRoutingForTests;
const app_routes_1 = require("../../shared/app-routes");
/**
 * The page the current history entry represents. Needed because a Back press
 * out of a routed page lands on a URL that names no page, and the browser only
 * returns whatever state we stamped on that entry.
 */
let reflected = null;
/**
 * Show a specific page and hide others
 */
function showPage(page) {
    const pageId = `${page}-page`;
    const pageElement = document.getElementById(pageId);
    // Resolve the target BEFORE hiding anything. Hiding first and then failing
    // to find the target leaves every page hidden — a blank white screen with
    // no error, which is how a `currentPage` value that no longer matches a
    // container id used to present itself.
    if (!pageElement) {
        console.error(`[showPage] No #${pageId} container — staying on the current page. ` +
            `This means currentPage "${page}" and index.html have drifted ` +
            `(a stale bundle will also do it: hard-reload before debugging).`);
        return;
    }
    // Hide all pages, then reveal the target.
    const pages = document.querySelectorAll(".page");
    pages.forEach((p) => p.classList.add("hidden"));
    pageElement.classList.remove("hidden");
    syncLocation(page);
}
/**
 * Point the address bar at `page` (see `src/shared/app-routes.ts`).
 *
 * Idempotent in two directions, because `showPage` runs on every state change,
 * not only on a real navigation: it does nothing when the URL already names
 * this page, so re-renders neither spam history nor overwrite a deep link the
 * page set for itself. A page with no route falls back to "/".
 */
function syncLocation(page, segments = [], opts = {}) {
    const here = window.location.pathname;
    const match = (0, app_routes_1.matchAppRoute)(here);
    // Already pointing at this page: leave the URL alone, so a deep link the
    // page set for itself is not flattened back to the list on a re-render.
    // A page that deliberately returns to its list passes `flatten`.
    if (segments.length === 0 && match?.route.page === page && !(opts.flatten && match.segments.length)) {
        reflected = page;
        return;
    }
    const target = (0, app_routes_1.appRoutePath)(page, segments) ?? "/";
    if (here === target) {
        reflected = page;
        return;
    }
    // Stamp the entry being LEFT with the page that was showing. Most pages
    // have no URL of their own, so without this Back returns to "/" with no
    // record of whether that was events, order or the pair builder.
    if (reflected)
        window.history.replaceState({ page: reflected }, "");
    window.history.pushState({ page, segments }, "", target);
    reflected = page;
}
/**
 * Apply the current URL, and keep applying it on Back/Forward.
 *
 * `apply` is called with the page a URL names, or with the page stamped on the
 * history entry when Back leaves the routed area entirely — `null` there means
 * "nothing recorded, use the app's default". Returning to a plain "/" has to
 * move the app too: changing the address bar without changing the page is the
 * one outcome a Back press must never produce.
 *
 * Call once the app knows whether it is signed in: a page URL opened while
 * signed out must not jump past the login screen.
 */
function initAppRouting(apply) {
    const run = (event) => {
        const match = (0, app_routes_1.matchAppRoute)(window.location.pathname);
        if (match) {
            reflected = match.route.page;
            apply(match.route.page, match.segments);
            return;
        }
        // On first load a non-page URL is just the app's normal entry point;
        // only a Back press out of a routed page needs restoring.
        if (!event)
            return;
        const stamped = event.state?.page ?? null;
        reflected = stamped;
        apply(stamped, []);
    };
    window.addEventListener("popstate", (event) => run(event));
    run(null);
}
/** Test seam: forget which page the current history entry is showing. */
function __resetRoutingForTests() {
    reflected = null;
}
